/**
 * SCH-1 — canonical, bounded, audited manager edit of per-job actual
 * minutes.
 *
 * Core-100 R4 (CORE-AJ / CORE-AL): the write is no longer a browser UPDATE.
 * Measured job time is server-written (ticket_jobs.actual_minutes refuses an
 * authenticated UPDATE: ACTUAL_MINUTES_SERVER_ONLY). Each changed job goes
 * through `correct_ticket_completion(kind 'job_actual_minutes')`, which checks
 * manager/owner authority, requires a reason, records the previous value in the
 * append-only `ticket_completion_corrections` (supersession, CORE-AG) and writes
 * the audit_log row itself.
 *
 * `EditTimersModal` previously wrote `ticket_jobs.actual_minutes`
 * directly with: no value bounds (any number, even negative), no
 * `audit_log` row (who changed KPI-affecting actual time was
 * untraceable), and only a client-side React Query invalidation (other
 * devices kept stale time). This helper is the single path for that
 * edit: it validates/bounds the values, performs the writes, appends an
 * audit row, and broadcasts a board refresh so every device converges.
 *
 * The validation (`validateActualMinutesEdits`) is pure and unit-tested;
 * the orchestration takes an injected Supabase-like client so it can be
 * exercised without a live DB.
 */

import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';

/** A manager-entered actual time above one day is a data-entry error. */
export const MAX_ACTUAL_MINUTES = 24 * 60;

export interface ActualMinutesEditRejection {
  jobId: string;
  value: unknown;
  reason: string;
}

export interface ValidateActualMinutesResult {
  valid: Record<string, number>;
  rejected: ActualMinutesEditRejection[];
}

/**
 * Pure bounds check for a manager actual-minutes edit map. A value is
 * valid only when it is a finite, non-negative whole number ≤
 * MAX_ACTUAL_MINUTES. Everything else is rejected with a reason — and
 * the apply path refuses the whole save if anything is rejected, so no
 * partial/garbage write lands.
 */
export function validateActualMinutesEdits(
  edits: Record<string, unknown>,
): ValidateActualMinutesResult {
  const valid: Record<string, number> = {};
  const rejected: ActualMinutesEditRejection[] = [];
  for (const [jobId, raw] of Object.entries(edits)) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) {
      rejected.push({ jobId, value: raw, reason: 'not a number' });
    } else if (!Number.isInteger(n)) {
      rejected.push({ jobId, value: raw, reason: 'must be a whole number of minutes' });
    } else if (n < 0) {
      rejected.push({ jobId, value: raw, reason: 'must be 0 or more' });
    } else if (n > MAX_ACTUAL_MINUTES) {
      rejected.push({ jobId, value: raw, reason: `must be ${MAX_ACTUAL_MINUTES} minutes (24h) or less` });
    } else {
      valid[jobId] = n;
    }
  }
  return { valid, rejected };
}

export class InvalidActualMinutesError extends Error {
  rejected: ActualMinutesEditRejection[];
  constructor(rejected: ActualMinutesEditRejection[]) {
    super(
      `Invalid time value${rejected.length > 1 ? 's' : ''}: ` +
        rejected.map((r) => `job ${r.jobId} — ${r.reason}`).join('; '),
    );
    this.name = 'InvalidActualMinutesError';
    this.rejected = rejected;
  }
}

interface SupabaseLike {
  rpc: (fn: string, args: Record<string, unknown>) => any;
}

export interface ApplyManagerActualMinutesEditArgs {
  supabase: SupabaseLike;
  ticketId: string;
  shopId: string | null;
  actorUserId: string | null;
  edits: Record<string, unknown>;
  /** The values currently recorded, so unchanged jobs are not "corrected". */
  current?: Record<string, number | null | undefined>;
  /** Why the measured time is being corrected (required by the server). */
  reason: string;
  /** Injectable for tests; defaults to the real board broadcast. */
  notify?: typeof notifyDayboardInvalidated;
}

export interface ApplyManagerActualMinutesEditResult {
  updated: number;
  /** Every applied change was recorded server-side (correction row + audit_log) in the same transaction. */
  audited: boolean;
  correctionIds: string[];
}

/**
 * Apply a bounded, audited manager actual-minutes edit. Throws
 * `InvalidActualMinutesError` (writing nothing) if any value is invalid.
 * After the authoritative `ticket_jobs` writes it appends an
 * `audit_log` row (best-effort, mirroring the codebase audit pattern)
 * and broadcasts a board refresh.
 */
export async function applyManagerActualMinutesEdit(
  args: ApplyManagerActualMinutesEditArgs,
): Promise<ApplyManagerActualMinutesEditResult> {
  const { supabase, ticketId, shopId, edits, current } = args;
  void args.actorUserId; // the server takes the actor from the session, never from the caller
  const notify = args.notify ?? notifyDayboardInvalidated;

  const { valid, rejected } = validateActualMinutesEdits(edits);
  if (rejected.length > 0) throw new InvalidActualMinutesError(rejected);
  const reason = (args.reason ?? '').trim();
  if (reason.length < 3) {
    throw new InvalidActualMinutesError([{ jobId: '*', value: args.reason, reason: 'say why the time is being corrected' }]);
  }

  // Only real changes are corrections; an untouched row (or an unset one left at 0) is not.
  const changed = Object.entries(valid).filter(([jobId, minutes]) => {
    if (!current || !(jobId in current)) return true;
    return (current[jobId] ?? 0) !== minutes;
  });

  let updated = 0;
  const correctionIds: string[] = [];
  for (const [jobId, minutes] of changed) {
    const { data, error } = await supabase.rpc('correct_ticket_completion', {
      p_ticket_id: ticketId,
      p_kind: 'job_actual_minutes',
      p_job_id: jobId,
      p_value: { minutes },
      p_reason: reason,
    });
    if (error) throw error;
    const id = (data as { correction_id?: string } | null)?.correction_id;
    if (id) correctionIds.push(id);
    updated += 1;
  }
  const audited = correctionIds.length === updated;

  // SCH-1: cross-device board refresh (was client-query-only).
  if (shopId) {
    await notify({ reason: 'timer-update', shopId, ticketId });
  }

  return { updated, audited, correctionIds };
}
